import type {
  Cancelar,
  ErroApp,
  EstadoPosVenda,
  EventoUpdaterApp,
  PlataformaApp,
  RequisicaoApp,
  RespostaApp,
  ResultadoImpressaoApp,
} from './tipos';

/**
 * Ponte do app web: os mesmos caminhos que o desktop resolve por IPC
 * (`/api/clientes`, `/api/orcamentos/:id`, …) viram chamadas HTTP para
 * `<base>/app/api/...` no servidor.
 *
 * O prefixo `/app` separa o backend do aplicativo das rotas de
 * infraestrutura que já existem no servidor (`/api/auth`, `/api/sync`,
 * `/api/licenca`, landing, admin) — nada colide.
 *
 * Sessão: o desktop guarda o token cifrado no main process (safeStorage).
 * No navegador não existe equivalente, então guardamos em `localStorage` e
 * mandamos como `Authorization: Bearer`. É o mesmo modelo já usado pelo
 * painel de afiliados.
 */

const BASE_HTTP = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');
const PREFIXO = '/app';
const CHAVE_TOKEN = 'oficinaagil.token';

// -------------------------------------------------------------- sessão

export function obterToken(): string | null {
  try {
    return localStorage.getItem(CHAVE_TOKEN);
  } catch {
    // Navegador com storage bloqueado (aba anônima restrita, iframe).
    return null;
  }
}

function guardarToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(CHAVE_TOKEN, token);
    else localStorage.removeItem(CHAVE_TOKEN);
  } catch {
    // Sem storage a sessão dura só enquanto a aba estiver aberta.
  }
}

/**
 * O login/logout do app web acontece pelas mesmas rotas que o desktop usa,
 * mas quem precisa persistir o token aqui é o navegador. Em vez de vazar
 * essa responsabilidade pra `authApi`, interceptamos as duas rotas no
 * ponto onde a resposta passa.
 */
function reagirASessao(req: RequisicaoApp, corpo: unknown): void {
  if (req.metodo !== 'POST') return;
  if (req.caminho === '/api/auth/login') {
    const token = (corpo as { token?: string } | null)?.token;
    if (typeof token === 'string' && token) {
      guardarToken(token);
      // Só agora existe credencial pro socket de eventos.
      barramento.reconectar();
    }
    return;
  }
  if (req.caminho === '/api/auth/logout') {
    guardarToken(null);
    barramento.encerrar();
  }
}

// ---------------------------------------------------------------- HTTP

function montarUrl(req: RequisicaoApp): string {
  const url = new URL(`${BASE_HTTP}${PREFIXO}${req.caminho}`, window.location.origin);
  for (const [chave, valor] of Object.entries(req.query ?? {})) {
    if (valor !== undefined) url.searchParams.set(chave, valor);
  }
  return url.toString();
}

const ERRO_REDE: ErroApp = {
  codigo: 'rede.indisponivel',
  tipo: 'Indisponivel',
  mensagem: 'Sem conexão com o servidor. Verifique sua internet e tente novamente.',
};

/** O servidor responde `{ erro: { codigo, tipo, mensagem } }`; qualquer outra coisa vira genérico. */
function extrairErro(corpo: unknown, status: number): ErroApp {
  const bruto = (corpo as { erro?: unknown } | null)?.erro;
  if (bruto && typeof bruto === 'object' && 'mensagem' in bruto) {
    const e = bruto as Partial<ErroApp>;
    return {
      codigo: e.codigo ?? 'erro.inesperado',
      tipo: e.tipo ?? 'Inesperado',
      mensagem: e.mensagem ?? `Falha na operação (HTTP ${status}).`,
    };
  }
  return {
    codigo: 'erro.inesperado',
    tipo: 'Inesperado',
    mensagem: `Falha na operação (HTTP ${status}).`,
  };
}

/**
 * O app web não tem banco local: quando o servidor some, não existe "modo
 * offline" — existe app parado. Guardamos aqui se a última tentativa de
 * contato deu certo para a UI poder dizer isso com honestidade, em vez de
 * manter um "Conectado" verde que já não é verdade.
 */
let servidorAlcancavel = true;

function marcarConexao(alcancavel: boolean): void {
  if (servidorAlcancavel === alcancavel) return;
  servidorAlcancavel = alcancavel;
  barramento.emitir({ tipo: 'status' });
}

/** true quando o último contato com o servidor funcionou. */
export function servidorEstaAlcancavel(): boolean {
  return servidorAlcancavel;
}

async function request(req: RequisicaoApp): Promise<RespostaApp> {
  const token = obterToken();
  let resposta: Response;
  try {
    resposta = await fetch(montarUrl(req), {
      method: req.metodo,
      headers: {
        ...(req.corpo !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: req.corpo !== undefined ? JSON.stringify(req.corpo) : undefined,
      // Sessão vai por Bearer; não dependemos de cookie entre domínios.
      credentials: 'omit',
    });
  } catch {
    // Diferente do desktop, aqui não há banco local pra cair de volta:
    // sem rede o app web não tem o que mostrar. A mensagem precisa ser
    // clara em vez de um "Failed to fetch" cru.
    marcarConexao(false);
    return { status: 0, erro: ERRO_REDE };
  }

  // Respondeu — mesmo com erro de aplicação, o servidor está de pé. Se
  // estávamos marcados como sem conexão, o canal de eventos também deve
  // voltar: o socket pode ter caído junto com a rede.
  if (!servidorAlcancavel) {
    marcarConexao(true);
    barramento.reconectar();
  }

  if (resposta.status === 204) return { status: 204 };

  const texto = await resposta.text();
  let corpo: unknown = undefined;
  if (texto) {
    try {
      corpo = JSON.parse(texto);
    } catch {
      corpo = texto;
    }
  }

  if (resposta.status >= 400) {
    // O token expirou ou foi revogado: limpa a sessão pra UI voltar
    // pro login em vez de insistir com credencial morta. O socket usa a
    // mesma credencial, então cai junto — senão ficaria tentando reabrir
    // com um token que o servidor já recusa.
    if (resposta.status === 401) {
      guardarToken(null);
      barramento.encerrar();
    }
    return { status: resposta.status, erro: extrairErro(corpo, resposta.status) };
  }

  reagirASessao(req, corpo);
  return { status: resposta.status, corpo };
}

// ------------------------------------------------------------- eventos

type TipoNotificacao = 'status' | 'dados' | 'aviso' | 'posvenda' | 'posvenda.avaliacao';

type Notificacao =
  | { tipo: 'status' }
  | { tipo: 'dados' }
  | { tipo: 'aviso' }
  | { tipo: 'posvenda'; estado: EstadoPosVenda }
  | { tipo: 'posvenda.avaliacao'; orcamentoId: string | null };

type Ouvinte = (n: Notificacao) => void;

/**
 * Barramento de eventos do servidor. No desktop o main process empurra
 * `sync:status-mudou` e companhia pelo IPC; aqui a fonte é um WebSocket.
 *
 * Este canal é o que faz o navegador enxergar o que o desktop faz (e
 * vice-versa) sem F5, então ele precisa ser difícil de matar:
 *
 *   - **Heartbeat de aplicação.** O navegador responde ao ping do
 *     protocolo sozinho, mas o JS não consegue observar isso. Mandamos
 *     `{tipo:'ping'}` e exigimos `{tipo:'pong'}` de volta; sem resposta,
 *     o socket é considerado morto e reaberto. É o único jeito de
 *     detectar conexão que caiu sem `close` — Wi-Fi trocado, 4G→Wi-Fi,
 *     proxy que corta silenciosamente.
 *   - **Gatilhos do ambiente.** Voltar da aba em segundo plano e o evento
 *     `online` do navegador forçam verificação imediata: celular suspende
 *     timers, e esperar o backoff deixaria a tela desatualizada por
 *     minutos justamente quando o usuário voltou a olhar.
 *   - **Backoff com jitter** para não sincronizar todas as abas do mundo
 *     no mesmo instante quando o servidor reinicia.
 *
 * Se ainda assim o socket não subir, o app continua utilizável — perde a
 * atualização instantânea, que o polling do `useAuth` acaba cobrindo.
 */
const barramento = (() => {
  const ouvintes = new Set<Ouvinte>();
  let socket: WebSocket | null = null;
  let tentativas = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let encerrado = false;
  let vigia: ReturnType<typeof setInterval> | null = null;
  /** Quando pedimos o último `pong`; 0 quando não há sondagem em curso. */
  let pingEnviadoEm = 0;
  let ultimoSinal = 0;
  let ambienteAmarrado = false;

  /** Intervalo do heartbeat e prazo para o `pong` chegar. */
  const HEARTBEAT_MS = 25_000;
  const PRAZO_PONG_MS = 10_000;

  function urlWebSocket(): string {
    const base = BASE_HTTP || window.location.origin;
    const url = new URL(`${base}/ws/app`);
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    const token = obterToken();
    if (token) url.searchParams.set('token', token);
    return url.toString();
  }

  function agendarReconexao(): void {
    if (encerrado || timer) return;
    if (!obterToken()) return; // sem sessão não há o que reabrir
    // 1s, 2s, 4s… até 30s, com jitter de até 1s.
    const espera = Math.min(30_000, 1000 * 2 ** tentativas) + Math.random() * 1000;
    tentativas += 1;
    timer = setTimeout(() => {
      timer = null;
      conectar();
    }, espera);
  }

  /** Derruba o socket atual; o `onclose` agenda a reabertura. */
  function derrubar(): void {
    pararVigia();
    if (!socket) return;
    const antigo = socket;
    socket = null;
    // Tira os handlers antes de fechar: não queremos que o `onclose` do
    // socket velho conte como queda do novo.
    antigo.onopen = null;
    antigo.onmessage = null;
    antigo.onerror = null;
    antigo.onclose = null;
    try {
      antigo.close();
    } catch {
      // já estava morto
    }
    agendarReconexao();
  }

  /** Envia a sondagem de vida. Devolve false se nem isso foi possível. */
  function sondar(): boolean {
    if (!socket || socket.readyState !== WebSocket.OPEN) return false;
    try {
      socket.send(JSON.stringify({ tipo: 'ping' }));
      pingEnviadoEm = Date.now();
      return true;
    } catch {
      return false;
    }
  }

  function iniciarVigia(): void {
    pararVigia();
    vigia = setInterval(() => {
      if (!socket || socket.readyState !== WebSocket.OPEN) return;
      if (pingEnviadoEm && Date.now() - pingEnviadoEm > PRAZO_PONG_MS) {
        // Pedimos e não veio resposta: o canal está morto mesmo que o
        // navegador ainda diga OPEN.
        derrubar();
        return;
      }
      if (Date.now() - ultimoSinal < HEARTBEAT_MS) return;
      if (!sondar()) derrubar();
    }, HEARTBEAT_MS / 2);
  }

  function pararVigia(): void {
    if (vigia) {
      clearInterval(vigia);
      vigia = null;
    }
    pingEnviadoEm = 0;
  }

  function conectar(): void {
    if (encerrado || socket) return;
    if (!obterToken()) return; // sem sessão não há o que ouvir
    let ws: WebSocket;
    try {
      ws = new WebSocket(urlWebSocket());
    } catch {
      agendarReconexao();
      return;
    }
    socket = ws;

    ws.onopen = () => {
      tentativas = 0;
      ultimoSinal = Date.now();
      iniciarVigia();
      // O socket estar de pé é, por si só, prova de que o servidor
      // responde — o indicador de conexão pode voltar ao verde.
      marcarConexao(true);
    };
    ws.onmessage = (evento) => {
      ultimoSinal = Date.now();
      pingEnviadoEm = 0; // veio resposta: sondagem encerrada
      try {
        const dados = JSON.parse(String(evento.data)) as {
          tipo?: TipoNotificacao | 'pong' | 'conectado';
          estado?: EstadoPosVenda;
          orcamentoId?: string;
          dados?: { orcamentoId?: string };
        };
        if (!dados.tipo) return;
        // `pong` e `conectado` só valem como sinal de vida, já registrado.
        if (dados.tipo === 'pong' || dados.tipo === 'conectado') return;
        if (dados.tipo === 'posvenda') {
          emitir({
            tipo: 'posvenda',
            estado: dados.estado ?? { status: 'desconhecido', numero: null },
          });
          return;
        }
        if (dados.tipo === 'posvenda.avaliacao') {
          emitir({
            tipo: 'posvenda.avaliacao',
            orcamentoId: dados.orcamentoId ?? dados.dados?.orcamentoId ?? null,
          });
          return;
        }
        emitir({ tipo: dados.tipo } as Notificacao);
      } catch {
        // Mensagem fora do formato — ignora em vez de derrubar a conexão.
      }
    };
    ws.onclose = () => {
      socket = null;
      pararVigia();
      agendarReconexao();
    };
    ws.onerror = () => {
      try {
        ws.close();
      } catch {
        // onclose cuida da reconexão.
      }
    };
  }

  /**
   * Verifica agora se o canal está vivo, sem esperar o backoff. Chamado
   * quando a aba volta ao primeiro plano ou a rede retorna — os dois
   * momentos em que o usuário mais espera ver dados atuais.
   */
  function verificarAgora(): void {
    if (encerrado || !obterToken()) return;
    if (!socket) {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      tentativas = 0;
      conectar();
      return;
    }
    if (socket.readyState === WebSocket.CLOSED || socket.readyState === WebSocket.CLOSING) {
      derrubar();
      return;
    }
    if (socket.readyState === WebSocket.OPEN) {
      // Alternar janelas dispara `focus` o tempo todo; só vale sondar se
      // faz um tempo que não vem nada do servidor.
      if (Date.now() - ultimoSinal < 5_000) return;
      if (!sondar()) derrubar();
    }
  }

  /** Liga os gatilhos do navegador uma única vez. */
  function amarrarAoAmbiente(): void {
    if (ambienteAmarrado || typeof window === 'undefined') return;
    ambienteAmarrado = true;
    window.addEventListener('online', verificarAgora);
    window.addEventListener('focus', verificarAgora);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') verificarAgora();
    });
    // A aba fechando: encerra limpo para o servidor liberar o registro na
    // hora, em vez de esperar o heartbeat expirar.
    window.addEventListener('pagehide', () => {
      try {
        socket?.close();
      } catch {
        // nada a fazer com a aba indo embora
      }
    });
  }

  function emitir(n: Notificacao): void {
    for (const ouvinte of ouvintes) {
      try {
        ouvinte(n);
      } catch {
        // Um ouvinte quebrado não pode impedir os outros de receber.
      }
    }
  }

  function assinar(ouvinte: Ouvinte): Cancelar {
    ouvintes.add(ouvinte);
    amarrarAoAmbiente();
    conectar();
    return () => {
      ouvintes.delete(ouvinte);
    };
  }

  /** Chamado após login, quando passa a existir token pra autenticar o socket. */
  function reconectar(): void {
    encerrado = false;
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    pararVigia();
    if (socket) {
      const antigo = socket;
      socket = null;
      antigo.onclose = null;
      try {
        antigo.close();
      } catch {
        // ignora
      }
    }
    tentativas = 0;
    amarrarAoAmbiente();
    conectar();
  }

  /** Logout ou sessão recusada: para de tentar até haver token novo. */
  function encerrar(): void {
    encerrado = true;
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    pararVigia();
    if (socket) {
      const antigo = socket;
      socket = null;
      antigo.onclose = null;
      try {
        antigo.close(1000, 'sessão encerrada');
      } catch {
        // ignora
      }
    }
  }

  return { assinar, emitir, reconectar, encerrar };
})();

/** Reabre o canal de eventos depois do login (quando o token passa a existir). */
export function reconectarEventos(): void {
  barramento.reconectar();
}

function assinarTipo(tipo: TipoNotificacao, callback: () => void): Cancelar {
  return barramento.assinar((n) => {
    if (n.tipo === tipo) callback();
  });
}

// ---------------------------------------------------------- impressão

async function aguardarImpressao(): Promise<ResultadoImpressaoApp> {
  // O navegador não avisa se o usuário cancelou — `afterprint` dispara nos
  // dois casos. Reportamos sucesso e deixamos o feedback pro próprio
  // diálogo do sistema, que o usuário está vendo.
  const fim = new Promise<void>((resolve) => {
    const terminar = () => {
      window.removeEventListener('afterprint', terminar);
      resolve();
    };
    window.addEventListener('afterprint', terminar);
    // Alguns navegadores móveis não emitem `afterprint`; não travamos.
    setTimeout(terminar, 60_000);
  });
  window.print();
  await fim;
  return { ok: true, cancelado: false };
}

// --------------------------------------------------------- plataforma

export const plataformaWeb: PlataformaApp = {
  capacidades: {
    alvo: 'web',
    autoUpdate: false,
    backupLocal: false,
    offline: false,
    impressaoTermica: false,
    salvarPdfNativo: false,
    historicoMensagens: false,
    tipoRoteador: 'browser',
    ferramentasSync: false,
  },

  // Injetada pelo Vite (`define`) a partir do package.json.
  versao: __APP_VERSION__,

  request,

  aoMudarStatus: (callback) => assinarTipo('status', callback),

  servidorAlcancavel: () => servidorAlcancavel,
  aoAtualizarDados: (callback) => assinarTipo('dados', callback),
  aoMudarAviso: (callback) => assinarTipo('aviso', callback),

  aoMudarPosVenda: (callback: (estado: EstadoPosVenda) => void) =>
    barramento.assinar((n) => {
      if (n.tipo === 'posvenda') callback(n.estado);
    }),

  aoAvaliacaoPosVenda: (callback: (orcamentoId: string | null) => void) =>
    barramento.assinar((n) => {
      if (n.tipo === 'posvenda.avaliacao') callback(n.orcamentoId);
    }),

  // `pageSize` é ignorado: o navegador não deixa forçar tamanho de papel.
  // O CSS `@page` injetado pelas telas de impressão continua valendo, o
  // que cobre a maior parte dos casos de cupom.
  imprimir: () => aguardarImpressao(),

  // Não dá pra escolher o caminho do arquivo pelo navegador — o usuário
  // seleciona "Salvar como PDF" no destino do próprio diálogo.
  salvarPdf: () => aguardarImpressao(),

  updater: {
    // No navegador a versão nova chega no F5; não há ciclo de update.
    aoEvento: (callback: (evento: EventoUpdaterApp) => void) => {
      void callback;
      return () => undefined;
    },
    checarAgora: async () => ({ ok: true }) as const,
    instalarAgora: async () => {
      window.location.reload();
      return { ok: true } as const;
    },
  },
};
